import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Settings, Wallet, CreditCard, Building2, 
  Briefcase, UserX, Users, Package, Truck, Palette,
  Check, X, Bell, Hash, Percent, Calendar, CalendarCheck,
  Utensils, Clock, FileText, Banknote, Globe, Star, Shield
} from 'lucide-react';
// ExchangeRatesCard supprimé - devises gérées dans /admin/settings

// Icônes par catégorie
const CATEGORY_ICONS = {
  CHANNEL: Building2,
  RESERVATION_STATUS: CalendarCheck,
  EVENT_TYPE: Utensils,
  EVENT_STATUS: Star,
  PRECHECKIN_CONFIG: FileText,
  FLUX_TYPE: Wallet,
  PAYMENT_METHOD: CreditCard,
  PAYMENT_TYPE: Banknote,
  // CURRENCY: removed - now managed at system level
  CHARGE_FREQUENCY: Calendar,
  TOURIST_TAX: Percent,
  TAX_RATE: Percent,
  POS: Package,
  CONTRACT_TYPE: Briefcase,
  JOB_POSITION: Users,
  ABSENCE_REASON: UserX,
  LEAVE_TYPE: Calendar,
  TIME_ENTRY_STATUS: Clock,
  UNIT: Package,
  DOCUMENT_TYPE: FileText,
  SUPPLIER: Truck,
  ALERT_THRESHOLD: Bell,
  NUMBERING: Hash
};

// Groupes de catégories
// Note: CURRENCY a été retiré - gérée maintenant via /admin/settings (devises système)
const CATEGORY_GROUPS = {
  'Réservations': ['CHANNEL', 'RESERVATION_STATUS', 'EVENT_TYPE', 'EVENT_STATUS', 'PRECHECKIN_CONFIG'],
  'Paiements': ['FLUX_TYPE', 'PAYMENT_METHOD', 'PAYMENT_TYPE', 'CHARGE_FREQUENCY'],
  'Taxes & Fiscalité': ['TOURIST_TAX', 'TAX_RATE'],
  'Point de vente': ['POS'],
  'Ressources Humaines': ['CONTRACT_TYPE', 'JOB_POSITION', 'ABSENCE_REASON', 'LEAVE_TYPE', 'TIME_ENTRY_STATUS'],
  'Produits & Documents': ['UNIT', 'DOCUMENT_TYPE', 'SUPPLIER'],
  'Système': ['ALERT_THRESHOLD', 'NUMBERING']
};

// Couleurs disponibles
const COLOR_OPTIONS = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
  '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
  '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
  '#EC4899', '#F43F5E', '#6B7280', '#374151', '#000000'
];

export default function ConfigurationTab() {
  const [categories, setCategories] = useState({});
  const [configData, setConfigData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('CHANNEL');
  const [showDialog, setShowDialog] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    label: '',
    color: '#6B7280',
    metadata: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, configRes] = await Promise.all([
        api.get('/config/categories'),
        api.get('/config')
      ]);
      setCategories(categoriesRes.data);
      setConfigData(configRes.data);
      
      if (!configRes.data[activeCategory]) {
        const firstKey = Object.keys(configRes.data)[0] || Object.keys(categoriesRes.data)[0];
        if (firstKey) setActiveCategory(firstKey);
      }
    } catch (error) {
      try {
        await api.post('/config/initialize');
        fetchData();
      } catch (initError) {
        toast.error('Erreur lors du chargement de la configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ code: '', label: '', color: '#6B7280', metadata: {} });
    setEditingOption(null);
  };

  const openAddDialog = () => {
    resetForm();
    const categoryInfo = categories[activeCategory];
    if (categoryInfo?.metadataSchema) {
      const defaultMetadata = {};
      for (const [key, schema] of Object.entries(categoryInfo.metadataSchema)) {
        if (schema.default !== undefined) {
          defaultMetadata[key] = schema.default;
        }
      }
      setFormData(prev => ({ ...prev, metadata: defaultMetadata }));
    }
    setShowDialog(true);
  };

  const openEditDialog = (option) => {
    setEditingOption(option);
    setFormData({
      code: option.code,
      label: option.label,
      color: option.color || '#6B7280',
      metadata: option.metadata || {}
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.label.trim()) {
      toast.error('Le libellé est requis');
      return;
    }

    try {
      if (editingOption) {
        await api.put(`/config/${editingOption.id}`, {
          label: formData.label,
          color: formData.color,
          metadata: formData.metadata
        });
        toast.success('Option mise à jour');
      } else {
        if (!formData.code.trim()) {
          toast.error('Le code est requis');
          return;
        }
        await api.post('/config', {
          category: activeCategory,
          code: formData.code.toUpperCase().replace(/\s+/g, '_'),
          label: formData.label,
          color: formData.color,
          metadata: formData.metadata
        });
        toast.success('Option créée');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (option) => {
    const message = option.is_system 
      ? 'Cet élément système sera désactivé (non supprimé). Continuer ?'
      : 'Supprimer cette option ?';
    
    if (!window.confirm(message)) return;

    try {
      await api.delete(`/config/${option.id}`);
      toast.success(option.is_system ? 'Option désactivée' : 'Option supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (option) => {
    try {
      await api.put(`/config/${option.id}`, { is_active: !option.is_active });
      toast.success(option.is_active ? 'Option désactivée' : 'Option activée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const renderMetadataFields = () => {
    const categoryInfo = categories[activeCategory];
    if (!categoryInfo?.metadataSchema) return null;

    return Object.entries(categoryInfo.metadataSchema).map(([key, schema]) => {
      if (schema.type === 'boolean') {
        return (
          <div key={key} className="flex items-center justify-between">
            <Label>{schema.label}</Label>
            <Switch
              checked={formData.metadata[key] || false}
              onCheckedChange={(checked) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, [key]: checked }
              })}
            />
          </div>
        );
      }
      if (schema.type === 'number') {
        return (
          <div key={key}>
            <Label>{schema.label}</Label>
            <Input
              type="number"
              value={formData.metadata[key] || ''}
              onChange={(e) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, [key]: parseFloat(e.target.value) || 0 }
              })}
            />
          </div>
        );
      }
      if (schema.type === 'string') {
        return (
          <div key={key}>
            <Label>{schema.label}</Label>
            <Input
              value={formData.metadata[key] || ''}
              onChange={(e) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, [key]: e.target.value }
              })}
            />
          </div>
        );
      }
      if (schema.type === 'text') {
        return (
          <div key={key}>
            <Label>{schema.label}</Label>
            <textarea
              className="w-full p-2 border rounded-md bg-background min-h-[120px] text-sm"
              value={formData.metadata[key] || ''}
              onChange={(e) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, [key]: e.target.value }
              })}
              placeholder="Entrez le contenu..."
            />
          </div>
        );
      }
      if (schema.type === 'select' && schema.options) {
        let selectOptions = [];
        if (Array.isArray(schema.options)) {
          selectOptions = schema.options.map(val => ({ code: val, label: val }));
        } else if (typeof schema.options === 'string') {
          selectOptions = configData[schema.options]?.options || [];
        }
        
        return (
          <div key={key}>
            <Label>{schema.label}</Label>
            <select
              className="w-full p-2 border rounded-md bg-background"
              value={formData.metadata[key] || schema.default || ''}
              onChange={(e) => setFormData({
                ...formData,
                metadata: { ...formData.metadata, [key]: e.target.value }
              })}
            >
              <option value="">Sélectionner...</option>
              {selectOptions.map((opt) => (
                <option key={opt.code} value={opt.code}>{opt.label}</option>
              ))}
            </select>
          </div>
        );
      }
      return null;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentCategoryData = configData[activeCategory];
  const currentCategoryInfo = categories[activeCategory];
  const CategoryIcon = CATEGORY_ICONS[activeCategory] || Settings;

  return (
    <div className="space-y-4" data-testid="configuration-tab">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar - Categories grouped */}
        <div className="lg:w-64 shrink-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Catégories</CardTitle>
            </CardHeader>
            <CardContent className="p-2 max-h-[500px] overflow-y-auto">
              <div className="space-y-4">
                {Object.entries(CATEGORY_GROUPS).map(([groupName, groupCategories]) => {
                  const availableCategories = groupCategories.filter(key => categories[key]);
                  if (availableCategories.length === 0) return null;
                  
                  return (
                    <div key={groupName}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
                        {groupName}
                      </p>
                      <div className="space-y-1">
                        {availableCategories.map((key) => {
                          const cat = categories[key];
                          const Icon = CATEGORY_ICONS[key] || Settings;
                          const optionsCount = configData[key]?.options?.length || 0;
                          const hasSystemOptions = configData[key]?.options?.some(o => o.is_system);
                          return (
                            <button
                              key={key}
                              onClick={() => setActiveCategory(key)}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                                activeCategory === key 
                                  ? 'bg-primary text-primary-foreground' 
                                  : 'hover:bg-muted'
                              }`}
                              data-testid={`category-btn-${key}`}
                            >
                              <Icon className="w-4 h-4 shrink-0" />
                              <span className="flex-1 text-sm font-medium truncate">{cat.label}</span>
                              <div className="flex items-center gap-1">
                                {hasSystemOptions && (
                                  <Shield className={`w-3 h-3 ${activeCategory === key ? 'text-primary-foreground/70' : 'text-muted-foreground'}`} />
                                )}
                                <Badge variant={activeCategory === key ? 'secondary' : 'outline'} className="text-xs">
                                  {optionsCount}
                                </Badge>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <CategoryIcon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle>{currentCategoryInfo?.label || activeCategory}</CardTitle>
                    <p className="text-sm text-muted-foreground">{currentCategoryInfo?.description}</p>
                  </div>
                </div>
                <Button onClick={openAddDialog} data-testid="add-option-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!currentCategoryData?.options?.length ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune option configurée</p>
                  <p className="text-sm">Cliquez sur "Ajouter" pour créer une option</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {currentCategoryData.options.map((option) => (
                    <div
                      key={option.id}
                      className={`flex items-center gap-4 p-4 border rounded-lg transition-colors ${
                        !option.is_active ? 'opacity-50 bg-muted/50' : 'hover:bg-muted/30'
                      }`}
                      data-testid={`option-${option.code}`}
                    >
                      <div 
                        className="w-4 h-4 rounded-full shrink-0 border"
                        style={{ backgroundColor: option.color || '#6B7280' }}
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{option.label}</span>
                          <Badge variant="outline" className="text-xs font-mono">
                            {option.code}
                          </Badge>
                          {option.is_system && (
                            <Badge variant="secondary" className="text-xs">Système</Badge>
                          )}
                          {!option.is_active && (
                            <Badge variant="destructive" className="text-xs">Inactif</Badge>
                          )}
                        </div>
                        
                        {option.metadata && Object.keys(option.metadata).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {Object.entries(option.metadata).map(([key, value]) => {
                              const schema = currentCategoryInfo?.metadataSchema?.[key];
                              if (!schema) return null;
                              
                              // For bilingual text fields, show a condensed version
                              if (key === 'text_fr' || key === 'text_en') {
                                const flag = key === 'text_fr' ? '🇫🇷' : '🇬🇧';
                                const preview = value ? (value.length > 30 ? value.substring(0, 30) + '...' : value) : '(vide)';
                                return (
                                  <span key={key} className="text-xs text-muted-foreground">
                                    {flag} {preview}
                                  </span>
                                );
                              }
                              
                              // For time fields
                              if (key === 'time' && value) {
                                return (
                                  <span key={key} className="text-xs font-mono bg-muted px-2 py-0.5 rounded">
                                    {value}
                                  </span>
                                );
                              }
                              
                              let displayValue = value;
                              if (schema.type === 'select' && schema.options) {
                                if (typeof schema.options === 'string' && configData[schema.options]) {
                                  const refOption = configData[schema.options].options?.find(f => f.code === value);
                                  displayValue = refOption?.label || value;
                                }
                              }
                              
                              return (
                                <span key={key} className="text-xs text-muted-foreground">
                                  {schema.label}: {typeof value === 'boolean' ? (value ? 'Oui' : 'Non') : displayValue}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleActive(option)}
                          title={option.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {option.is_active ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <X className="w-4 h-4 text-red-500" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(option)}
                          data-testid={`edit-option-${option.code}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {!option.is_system && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(option)}
                            data-testid={`delete-option-${option.code}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingOption ? 'Modifier l\'option' : 'Nouvelle option'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingOption && (
              <div>
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                  placeholder="EX: NOUVEAU_TYPE"
                  className="font-mono"
                  required
                  data-testid="option-code-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Identifiant unique (lettres majuscules et underscores)
                </p>
              </div>
            )}
            
            <div>
              <Label>Libellé *</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Ex: Nouveau type"
                required
                data-testid="option-label-input"
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Couleur
              </Label>
              <div className="grid grid-cols-10 gap-1 mt-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-6 h-6 rounded border-2 transition-all ${
                      formData.color === color 
                        ? 'border-primary scale-110' 
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {renderMetadataFields()}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="save-option-btn">
                {editingOption ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
